import { useCallback, useEffect, useMemo, useRef } from "react"
import {
  ColumnDef,
  getCoreRowModel,
  getExpandedRowModel,
  Table,
  useReactTable
} from "@tanstack/react-table"
import {
  useVirtualizer,
  useWindowVirtualizer,
  Virtualizer
} from "@tanstack/react-virtual"
import {
  Column,
  ScrollProps,
  SortProps,
  SortTypes,
  TableRecordType
} from "../types"

export type VirtualTableOptions = SortProps &
  ScrollProps & {
    size?: "sm" | "lg"
    headerColumns: Column[]
    displayedProperties?: string[]
    records: TableRecordType[]
    loading?: boolean
    expandable?: boolean
    // container-based virtualization instead of window-based
    useContainerScroll?: boolean
  }

export type VirtualTableConfig = {
  overscan: number
  scrollThreshold: number
}

export type UseVirtualTable = {
  tableContainerRef: React.MutableRefObject<HTMLDivElement>
  table: Table<TableRecordType>
  rowVirtualizer:
    | Virtualizer<Window, Element>
    | Virtualizer<HTMLElement, Element>
  onSort: (sortBy: string, order: SortTypes) => void
}

const getTableColumns = (
  headerColumns: Column[]
): ColumnDef<TableRecordType>[] => {
  if (!headerColumns) {
    return []
  }

  return headerColumns.map<ColumnDef<TableRecordType>>(
    ({
      name,
      sortableName,
      label,
      displayLabel,
      sortable,
      size,
      canResize = true,
      tooltip,
      align,
      mask,
      columns,
      accessorPath,
      diagonalHeader
    }) => {
      // For properties with dots (e.g., "content_view_list.test"), we need accessorFn
      // TanStack Table's accessorKey doesn't support dot notation
      const hasDotNotation = name?.includes(".")
      const needsAccessorFn = accessorPath || hasDotNotation

      return {
        header: displayLabel || label, // Use displayLabel for table header if provided, otherwise label
        // Use accessorFn for nested data (accessorPath) or dot notation properties
        accessorFn: needsAccessorFn
          ? (record: TableRecordType) => {
              // If we have an accessorPath, use it (for nested Standards/Categories)
              if (accessorPath) {
                return accessorPath.reduce(
                  (nestedObj: any, propertyKey: string) =>
                    nestedObj?.[propertyKey],
                  record
                )
              }
              // Otherwise, just access the property directly (for dot notation like "content_view_list.test")
              return record[name]
            }
          : undefined,
        // Only use accessorKey for simple property names (no dots, no accessorPath)
        accessorKey: !needsAccessorFn ? name : undefined,
        id: name,
        sortableName,
        enableSorting: !!sortable,
        size,
        enableResizing: canResize,
        columns: getTableColumns(columns),
        diagonalHeader,
        meta: {
          tooltip,
          align: align ? align : "left",
          mask
        }
      }
    }
  )
}

export const useVirtualTable = (
  options: VirtualTableOptions,
  config?: VirtualTableConfig
): UseVirtualTable => {
  const {
    headerColumns,
    displayedProperties,
    records,
    size,
    sortBy,
    order,
    onSort,
    loading,
    hasMoreItems,
    onFetch,
    expandable,
    useContainerScroll = false
  } = options
  const { overscan = 5, scrollThreshold = 500 } = config ?? {}

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const reactTableColumns = useMemo(
    () => getTableColumns(headerColumns),
    [headerColumns]
  )

  const sortingState = useMemo(
    () => [{ id: sortBy, desc: order === "desc" }],
    [sortBy, order]
  )
  const columnVisibilityState = useMemo(() => {
    // Recursively build visibility state for all columns (including nested children)
    const buildVisibilityState = (cols: Column[]): Record<string, boolean> => {
      return cols.reduce((acc, col) => {
        // Add this column's visibility
        acc[col.name] = displayedProperties
          ? displayedProperties.includes(col.name)
          : true

        // Recursively add child columns if they exist
        if (col.columns && col.columns.length > 0) {
          Object.assign(acc, buildVisibilityState(col.columns))
        }

        return acc
      }, {} as Record<string, boolean>)
    }

    return buildVisibilityState(headerColumns)
  }, [headerColumns, displayedProperties])

  const table = useReactTable<TableRecordType>({
    columns: reactTableColumns,
    data: records,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: row => expandable && !!row.original.children?.length,
    getSubRows: row => row.children,
    manualSorting: true,
    state: {
      sorting: sortingState,
      columnVisibility: columnVisibilityState
    }
  })

  // Use container-based virtualization if useContainerScroll is true
  const rowVirtualizer = useContainerScroll
    ? useVirtualizer({
        count: table.getRowModel().rows.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => (size === "sm" ? 38 : 48),
        measureElement: element => element?.getBoundingClientRect().height,
        overscan
      })
    : useWindowVirtualizer({
        count: table.getRowModel().rows.length,
        estimateSize: () => (size === "sm" ? 38 : 48),
        measureElement: element => element?.getBoundingClientRect().height,
        scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
        overscan
      })

  // When sorting is changed, scroll to the top.
  const handleSort = useCallback(
    (sortBy: string, order: SortTypes) => {
      onSort?.(sortBy, order)
      rowVirtualizer.scrollToIndex(0)
    },
    [onSort, rowVirtualizer]
  )

  // Check if last virtual item is visible and is after currently loaded items.
  // If yes, fetch more items.
  useEffect(() => {
    const lastItem = [...rowVirtualizer.getVirtualItems()].at(-1)
    if (!lastItem) {
      return
    }

    if (lastItem.index >= records.length - 1 && hasMoreItems && !loading) {
      onFetch()
    }
  }, [
    hasMoreItems,
    onFetch,
    records.length,
    loading,
    rowVirtualizer.getVirtualItems()
  ])

  return {
    tableContainerRef,
    table,
    rowVirtualizer,
    onSort: handleSort
  }
}
